// BUILD-046 task 8.17 (supporting model): what the read-only Oversight_View / Team Work content shows for each row
// (design "Views and shell"; Req 4.18, 7.6, 7.7, 7.12, 14.17, 14.19, 14.30).
//
// The row content is read only. The action controls (task 8.25) are `controls`, decided by oversightActionsModel.ts from the Caller summary and
// the row's fields; a row described without the Caller summary has none. Oversight never adds anything to the viewer's own lists or counts:
// this module only describes rows the oversight route returned.
//
//  - Title: "Team Work (company oversight)" for the Company_Oversight_Scope holder, "Team Work" for everyone else.
//  - Department: a CLIENT-SIDE filter over the returned rows (the server ignores the legacy department parameter on the new routes).
//  - "Reassignment needed": shown from the server's `reassignmentNeeded` flag only; never inferred.
//  - Unclaimed Shared_Tasks are labelled unclaimed and show their recipient label instead of an owner.
//  - Owner-left indicator: the server's text as given (`ownerLeft[].text`); the client never builds, edits or dates it. (D1 display cleanup: only the "(FICTIONAL)"
//    test marker of the group name inside it is not shown.)
//  - People (D1): the assignee and the requester are shown First name + last initial (displayNames.personDisplay), the full name goes to the hover / focus text, and the
//    "(FICTIONAL)" test marker is not shown anywhere. Search still reads the stored names (viewSearchModel).
//  - Source and evidence links resolve with the VIEWER's own authorization (Req 14.17): the server marks each one available or not. An
//    available link with a URL is a link; an unavailable one is a non-activatable "unavailable" element that names nothing; a task whose
//    restricted source the viewer cannot open (`redacted`) shows only identity, state and the minimal reassignment indication (Req 14.30).
//
// Pure module: no React, no I/O, no runtime imports beyond the row helpers. Erasable TypeScript only.

import { offeredControls } from './oversightActionsModel';
import type { ActionControl, ActionMe } from './oversightActionsModel';
import { describeDue, recipientFullLabelOf, recipientGroupLabelOf, recipientLabelOf, recipientPeopleOf, relativeAge } from './taskRowModel';
import type { RecipientPerson } from './taskRowModel';
import { personDisplay, stripTestMarker } from './displayNames';

export const DEPARTMENTS: readonly { value: string; label: string }[] = Object.freeze([
  { value: 'sales', label: 'Sales' },
  { value: 'cad', label: 'CAD / Permit' },
  { value: 'project-management', label: 'Project Management' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'accounting', label: 'Accounting' },
]);

export const TEAM_WORK_TITLE = 'Team Work';
export const COMPANY_OVERSIGHT_TITLE = 'Team Work (company oversight)';

export function oversightTitle(me: { companyOversight?: boolean } | null | undefined): string {
  return me?.companyOversight === true ? COMPANY_OVERSIGHT_TITLE : TEAM_WORK_TITLE;
}

export interface OversightItem {
  id: string;
  title: string;
  status: string;
  priority?: string;
  department?: string;
  ownerName?: string;
  ownerEmployeeId?: string;
  requesterName?: string;
  recipientLabel?: string;
  groupRefs?: readonly { name: string }[];
  recipientSummary?: readonly { name?: string; employeeId?: string }[];
  assignmentKind?: string;
  unclaimed?: boolean;
  reassignmentNeeded?: boolean;
  redacted?: boolean;
  /** Read by the origin chip (a source-owned task, whose source link is resolved with the viewer's own authorization). */
  sourceRef?: { module?: string | null } | null;
  sourceGoverned?: boolean;
  lifecycle?: string;
  dueDate?: string;
  overdue?: boolean;
  nextAction?: string;
  waitingOn?: string;
  updatedAt?: string;
  projectDisplayName?: string;
  ownerLeft?: readonly { text?: unknown }[];
  sourceContext?: { available?: boolean; label?: string | null; url?: string | null; simulated?: boolean };
  evidence?: readonly { kind?: string; available?: boolean; name?: string; url?: string; simulated?: boolean }[];
}

/** The department filter. An empty department keeps every row; a department keeps only rows carrying exactly that department. */
export function filterByDepartment<T extends { department?: string }>(items: readonly T[], department: string): readonly T[] {
  return department === '' ? items : items.filter((item) => item.department === department);
}

export type LinkView =
  | { kind: 'link'; label: string; url: string; simulated: boolean }
  | { kind: 'text'; label: string; simulated: boolean }
  | { kind: 'unavailable'; label: string };

/** A usable URL is http(s) only: nothing else (javascript:, data:, a relative path) becomes an anchor. */
export function safeUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return /^https?:\/\/[^\s]+$/i.test(trimmed) ? trimmed : null;
}

export function describeSourceLink(item: OversightItem): LinkView | null {
  if (item.redacted === true) return null;
  const context = item.sourceContext;
  if (context === undefined || context === null) return null;
  if (context.available !== true) return { kind: 'unavailable', label: 'Source unavailable' };
  const label = typeof context.label === 'string' && stripTestMarker(context.label) !== '' ? stripTestMarker(context.label) : 'Source record';
  const url = safeUrl(context.url);
  return url === null ? { kind: 'text', label, simulated: context.simulated === true } : { kind: 'link', label, url, simulated: context.simulated === true };
}

export function describeEvidenceLinks(item: OversightItem): LinkView[] {
  if (item.redacted === true || !Array.isArray(item.evidence)) return [];
  return item.evidence.map((entry): LinkView => {
    if (entry === null || typeof entry !== 'object' || entry.available !== true) return { kind: 'unavailable', label: 'Evidence unavailable' };
    const label = typeof entry.name === 'string' && stripTestMarker(entry.name) !== '' ? stripTestMarker(entry.name) : 'Evidence';
    const url = safeUrl(entry.url);
    return url === null ? { kind: 'text', label, simulated: entry.simulated === true } : { kind: 'link', label, url, simulated: entry.simulated === true };
  });
}

/** The server's owner-left texts, exactly as given and only the non-empty strings. Never for a redacted task. */
export function ownerLeftTexts(item: OversightItem): string[] {
  if (item.redacted === true || !Array.isArray(item.ownerLeft)) return [];
  const out: string[] = [];
  // The server's wording is kept; only the "(FICTIONAL)" test marker of the group name inside it is not shown.
  for (const entry of item.ownerLeft) if (entry !== null && typeof entry === 'object' && typeof entry.text === 'string' && stripTestMarker(entry.text) !== '') out.push(stripTestMarker(entry.text));
  return out;
}

export interface OversightRowView {
  id: string;
  title: string;
  status: string;
  priority: string | null;
  project: string | null;
  /** `Owner: Eddie`, or `Not yet taken` for an unclaimed Shared_Task. The person is written First name + last initial (D1: displayNames.personDisplay). */
  ownerText: string;
  /** The same fact in the Team row's wording (correction cycle 1bb88b3e, N9): `Assignee: Eddie`, or `Not yet taken`. */
  assigneeText: string;
  /** The assignee as shown in the "Assigned to" cell (`Eddie`, `Alex S.`); null for an unclaimed task. `Unknown` when the task names no owner. */
  assigneeName: string | null;
  /** The assignee's full name (the "(FICTIONAL)" marker removed) for the hover and focus text; null when it adds nothing to `assigneeName`. */
  assigneeFull: string | null;
  unclaimed: boolean;
  /** The recipient label of an unclaimed task: group names without the marker, people First name + last initial. */
  recipientText: string | null;
  /** The same label with the people's full names, or null when it adds nothing. */
  recipientFullText: string | null;
  /**
   * Increment 1b-i (initials circles): the task's owner (the assignee) as the circle needs them ({ employeeId, name } - the stored name, the id '' when the task carries none), or null for an
   * unclaimed task and for a task that names no owner (the cell then writes the words `assigneeName` / "Not yet taken"). Named `ownerPerson` because the 8.25 guard keeps every
   * field that starts with "assign" (other than the three text fields) out of this row view: nothing here is an action.
   */
  ownerPerson: RecipientPerson | null;
  /** An unclaimed task's recipients that are PEOPLE, for the circles of the "Assigned to" cell; [] for a claimed task. */
  recipientPeople: RecipientPerson[];
  /** The "To ..." words of an unclaimed task that are NOT people: the group names (a group is not a person, so it stays text); null when none. */
  recipientGroupText: string | null;
  requesterText: string | null;
  reassignmentNeeded: boolean;
  /** True when the viewer may not open the task's restricted source: only identity, state and the minimal indication show. */
  minimal: boolean;
  due: { text: string | null; overdue: boolean };
  nextAction: string | null;
  waitingOn: string | null;
  updatedText: string;
  ownerLeft: string[];
  source: LinkView | null;
  evidence: LinkView[];
  /** The action controls offered to the Caller on this row (task 8.25); empty when the Caller summary was not given. */
  controls: ActionControl[];
}

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

export function describeOversightRow(item: OversightItem, nowMs: number, me?: ActionMe | null): OversightRowView {
  const minimal = item.redacted === true;
  const unclaimed = item.unclaimed === true;
  const ownerName = nonEmpty(item.ownerName);
  const requester = nonEmpty(item.requesterName);
  // D1 display cleanup: people are SHOWN as First name + last initial (or the first name alone), the full name is kept for the hover and focus text, and the
  // "(FICTIONAL)" test marker is not shown. The stored names (and what search reads: viewSearchModel) are the item's own and are not changed here.
  const owner = ownerName === null ? null : personDisplay(ownerName);
  const ownerShort = owner !== null && owner.short !== '' ? owner.short : 'Unknown';
  const ownerFull = owner !== null && owner.full !== '' && owner.full !== ownerShort ? owner.full : null;
  const requesterShort = requester === null ? '' : personDisplay(requester).short;
  return {
    id: item.id,
    title: stripTestMarker(item.title),
    status: item.status,
    priority: minimal ? null : nonEmpty(item.priority),
    project: minimal ? null : nonEmpty(stripTestMarker(item.projectDisplayName)),
    ownerText: unclaimed ? 'Not yet taken' : `Owner: ${ownerShort}`,
    assigneeText: unclaimed ? 'Not yet taken' : `Assignee: ${ownerShort}`,
    assigneeName: unclaimed ? null : ownerShort,
    assigneeFull: unclaimed ? null : ownerFull,
    unclaimed,
    recipientText: unclaimed ? recipientLabelOf(item) : null,
    recipientFullText: unclaimed ? recipientFullLabelOf(item) : null,
    ownerPerson: !unclaimed && ownerName !== null ? { employeeId: typeof item.ownerEmployeeId === 'string' ? item.ownerEmployeeId : '', name: ownerName } : null,
    recipientPeople: unclaimed ? recipientPeopleOf(item) : [],
    recipientGroupText: unclaimed ? recipientGroupLabelOf(item) : null,
    requesterText: requester === null ? null : `Requested by ${requesterShort !== '' ? requesterShort : stripTestMarker(requester)}`,
    reassignmentNeeded: item.reassignmentNeeded === true,
    minimal,
    due: minimal ? { text: null, overdue: false } : describeDue(item),
    nextAction: minimal ? null : nonEmpty(item.nextAction),
    waitingOn: minimal ? null : nonEmpty(item.waitingOn),
    updatedText: minimal ? '' : relativeAge(item.updatedAt, nowMs),
    ownerLeft: ownerLeftTexts(item),
    source: describeSourceLink(item),
    evidence: describeEvidenceLinks(item),
    controls: me === undefined || me === null ? [] : offeredControls(me, item, 'oversight').controls,
  };
}
